/**
 * Análisis por producto para la vista "Datos Generales".
 *
 * Cada hoja de ventas del Google Sheet es un producto: las filas llegan
 * etiquetadas con SHEET_NAME (ver fetchAllSalesRows en src/api/sheet.js).
 * Módulo sin estado propio — recibe los datos por argumentos desde general.js,
 * así re-renderiza igual con filtros, cambio de moneda y refresh.
 *
 * Convención de periodos:
 *   - "Pulso" y "ganadores" usan periodos de calendario ACTUALES (hoy, semana
 *     en curso desde lunes, mes en curso) sobre los datos SIN filtrar.
 *   - El ranking respeta el filtro Desde/Hasta de la vista.
 */

import { Chart } from 'chart.js/auto';
import { $ } from '../utils/dom.js';
import { fmtMoney, fmtInt, escapeHtml } from '../utils/format.js';
import { montoDisplay } from '../utils/currency.js';
import { startOfWeek, startOfMonth } from '../utils/date.js';

const MS_DAY = 86400000;
const DIAS_SIN_VENTA_WARN = 3;    // días sin vender para alertar (warn)
const DIAS_SIN_VENTA_BAD = 7;     // días sin vender para alertar (bad)
const DIAS_PRODUCTO_INACTIVO = 60; // más allá de esto el producto se considera retirado: sin alertas
const CONCENTRACION_PCT = 60;     // % del total en un solo producto que dispara la alerta
const TENDENCIA_BAJA_PCT = -30;   // caída vs su promedio semanal que dispara la alerta
const MAX_ALERTAS_PRODUCTO = 5;

// ─── Helpers de periodo ───────────────────────────────────────────────────

function startOfToday(now) {
  const x = new Date(now);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  return new Date(d.getTime() + n * MS_DAY);
}

/** Suma ventas con fecha en [from, to). Siempre en moneda de display. */
function sumPeriod(rows, from, to) {
  let monto = 0, count = 0, conMonto = 0;
  for (const r of rows) {
    if (!r.FECHA) continue;
    if (r.FECHA < from || r.FECHA >= to) continue;
    monto += montoDisplay(r);
    count++;
    if (r.MONTO > 0) conMonto++;
  }
  return { monto, count, conMonto };
}

function groupByProduct(rows) {
  const map = new Map();
  for (const r of rows) {
    const k = r.SHEET_NAME || '—';
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  }
  return map;
}

/**
 * Últimos 7 días del producto vs su propio promedio semanal histórico
 * (semanas desde su primera venta hasta el inicio de la semana en curso;
 * las semanas en cero también cuentan — son parte de "lo que normalmente
 * vende"). null si aún no hay una semana completa de historia.
 */
function weeklyTrend(productRows, now) {
  let first = null;
  for (const r of productRows) {
    if (r.FECHA && (!first || r.FECHA < first)) first = r.FECHA;
  }
  if (!first) return null;
  const firstWeek = startOfWeek(first);
  const cwStart = startOfWeek(now);
  const numWeeks = Math.round((cwStart - firstWeek) / (7 * MS_DAY));
  if (numWeeks < 1) return null;
  const avg = sumPeriod(productRows, firstWeek, cwStart).monto / numWeeks;
  if (avg <= 0) return null;
  const endOfToday = addDays(startOfToday(now), 1);
  const last7 = sumPeriod(productRows, addDays(endOfToday, -7), endOfToday).monto;
  return { pct: ((last7 - avg) / avg) * 100, last7, avg };
}

function lastSaleOf(rows) {
  let last = null;
  for (const r of rows) {
    if (r.FECHA && (!last || r.FECHA > last)) last = r.FECHA;
  }
  return last;
}

// ─── Pulso de ventas ──────────────────────────────────────────────────────

function deltaHtml(curr, prev) {
  if (prev > 0) {
    const pct = ((curr - prev) / prev) * 100;
    if (Math.abs(pct) < 0.05) return `vs ${fmtMoney(prev)} <span class="meta">=</span>`;
    // Subidas extremas: "×14.3" se lee mejor que "+1330%".
    if (pct >= 500) return `vs ${fmtMoney(prev)} <span class="delta-up">▲ ×${(curr / prev).toFixed(1)}</span>`;
    const up = pct > 0;
    return `vs ${fmtMoney(prev)} <span class="${up ? 'delta-up' : 'delta-down'}">${up ? '▲ +' : '▼ '}${pct.toFixed(1)}%</span>`;
  }
  if (curr > 0) return `<span class="delta-up">▲ nuevo</span>`;
  return 'sin ventas';
}

/** KPIs de momentum: periodo actual vs el anterior al mismo punto. */
export function renderPulso(allRows, now = new Date()) {
  const today = startOfToday(now);
  const end = addDays(today, 1); // fin de hoy: incluye todas las ventas de hoy
  const ws = startOfWeek(now);
  const ms = startOfMonth(now);

  // Mes anterior cortado al mismo día (clamp: 31 mar → 28 feb).
  const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const daysInPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  const prevMonthEnd = new Date(now.getFullYear(), now.getMonth() - 1, Math.min(now.getDate(), daysInPrevMonth) + 1);

  const kpis = [
    { id: 'gPulsoHoy', curr: sumPeriod(allRows, today, end), prev: sumPeriod(allRows, addDays(today, -1), today) },
    { id: 'gPulsoSemana', curr: sumPeriod(allRows, ws, end), prev: sumPeriod(allRows, addDays(ws, -7), addDays(end, -7)) },
    { id: 'gPulsoMes', curr: sumPeriod(allRows, ms, end), prev: sumPeriod(allRows, prevMonthStart, prevMonthEnd) },
  ];

  for (const k of kpis) {
    const el = $(k.id), sub = $(k.id + 'Sub');
    if (!el) continue;
    el.textContent = fmtMoney(k.curr.monto);
    if (sub) {
      sub.innerHTML = `${fmtInt(k.curr.count)} ${k.curr.count === 1 ? 'venta' : 'ventas'} · ${deltaHtml(k.curr.monto, k.prev.monto)}`;
    }
  }
}

// ─── Ganadores y ranking ──────────────────────────────────────────────────

/** Producto con más ventas en [from, to). Desempate: monto → #ventas → nombre. */
function winnerInPeriod(byProduct, from, to) {
  let best = null;
  for (const [name, rows] of byProduct) {
    const s = sumPeriod(rows, from, to);
    if (s.count === 0 && s.monto <= 0) continue;
    if (!best
      || s.monto > best.monto
      || (s.monto === best.monto && (s.count > best.count
        || (s.count === best.count && name.localeCompare(best.name) < 0)))) {
      best = { name, monto: s.monto, count: s.count };
    }
  }
  return best;
}

function renderWinnerCard(id, win, emptyMsg) {
  const el = $(id), sub = $(id + 'Sub');
  if (!el) return;
  if (!win) {
    el.textContent = '—';
    if (sub) sub.textContent = emptyMsg;
    return;
  }
  el.innerHTML = escapeHtml(win.name);
  if (sub) sub.textContent = `${fmtMoney(win.monto)} · ${fmtInt(win.count)} ${win.count === 1 ? 'venta' : 'ventas'}`;
}

/**
 * Producto con menos ventas en [from, to) entre los ACTIVOS (alguna venta en
 * los últimos DIAS_PRODUCTO_INACTIVO días) — no vender nada también es perder,
 * pero un producto retirado no debe acaparar la tarjeta para siempre.
 * Necesita 2+ candidatos. Desempates: monto → # ventas → racha más larga sin
 * vender → nombre.
 */
function loserInPeriod(byProduct, from, to, now) {
  const today = startOfToday(now);
  const candidates = [];
  for (const [name, rows] of byProduct) {
    const lastSale = lastSaleOf(rows);
    if (!lastSale) continue;
    const dias = Math.floor((today - startOfToday(lastSale)) / MS_DAY);
    if (dias >= DIAS_PRODUCTO_INACTIVO) continue;
    const s = sumPeriod(rows, from, to);
    candidates.push({ name, monto: s.monto, count: s.count, dias });
  }
  if (candidates.length < 2) return null;
  candidates.sort((a, b) =>
    (a.monto - b.monto) || (a.count - b.count) || (b.dias - a.dias) || a.name.localeCompare(b.name));
  return candidates[0];
}

function renderLoserCard(id, lose, emptyMsg) {
  const el = $(id), sub = $(id + 'Sub');
  if (!el) return;
  if (!lose) {
    el.textContent = '—';
    if (sub) sub.textContent = emptyMsg;
    return;
  }
  el.innerHTML = escapeHtml(lose.name);
  if (sub) {
    sub.textContent = (lose.monto > 0 || lose.count > 0)
      ? `${fmtMoney(lose.monto)} · ${fmtInt(lose.count)} ${lose.count === 1 ? 'venta' : 'ventas'}`
      : 'Sin ventas en el período';
  }
}

function buildRanking(byProductAll, ventasFiltered, now) {
  const byProductFiltered = groupByProduct(ventasFiltered);
  const totalFiltrado = ventasFiltered.reduce((s, r) => s + montoDisplay(r), 0);
  const today = startOfToday(now);

  const items = [];
  for (const [name, rowsAll] of byProductAll) {
    const rowsF = byProductFiltered.get(name) || [];
    let monto = 0, conMonto = 0;
    for (const r of rowsF) {
      monto += montoDisplay(r);
      if (r.MONTO > 0) conMonto++;
    }
    const trend = weeklyTrend(rowsAll, now);
    const lastSale = lastSaleOf(rowsAll);
    items.push({
      name,
      monto,
      count: rowsF.length,
      ticket: conMonto > 0 ? monto / conMonto : null,
      sharePct: totalFiltrado > 0 ? (monto / totalFiltrado) * 100 : null,
      trendPct: trend ? trend.pct : null,
      lastSale,
      diasSinVenta: lastSale ? Math.floor((today - startOfToday(lastSale)) / MS_DAY) : null,
    });
  }
  items.sort((a, b) => (b.monto - a.monto) || (b.count - a.count) || a.name.localeCompare(b.name));
  return { items, totalFiltrado };
}

function trendHtml(pct) {
  if (pct == null) return '<span class="meta">—</span>';
  if (Math.abs(pct) < 1) return '<span class="meta">=</span>';
  const up = pct > 0;
  return `<span class="${up ? 'delta-up' : 'delta-down'}">${up ? '▲ +' : '▼ '}${pct.toFixed(0)}%</span>`;
}

function lastSaleHtml(lastSale, dias) {
  if (!lastSale) return '<span class="meta">—</span>';
  const label = dias <= 0 ? 'hoy' : dias === 1 ? 'ayer' : `hace ${fmtInt(dias)} días`;
  return `<span title="${lastSale.toLocaleDateString('es-MX')}">${label}</span>`;
}

function renderRankingTable({ items, totalFiltrado }) {
  const t = $('gProdTable');
  if (!t) return;
  if (!items.length) {
    t.innerHTML = `<tbody><tr><td class="empty" colspan="7">Sin ventas registradas.</td></tr></tbody>`;
    return;
  }
  const totalCount = items.reduce((s, i) => s + i.count, 0);
  t.innerHTML = `
    <thead>
      <tr>
        <th>Producto</th>
        <th class="num">Ventas</th>
        <th class="num"># ventas</th>
        <th class="num">Ticket prom.</th>
        <th class="num">% del total</th>
        <th class="num">Tendencia 7d</th>
        <th>Última venta</th>
      </tr>
    </thead>
    <tbody>
      ${items.map((p, i) => `
        <tr${i === 0 && p.monto > 0 ? ' class="top-producto"' : ''}>
          <td title="${escapeHtml(p.name)}">${escapeHtml(p.name)}</td>
          <td class="num">${fmtMoney(p.monto)}</td>
          <td class="num">${fmtInt(p.count)}</td>
          <td class="num">${p.ticket != null ? fmtMoney(p.ticket) : '<span class="meta">—</span>'}</td>
          <td class="num">${p.sharePct != null ? p.sharePct.toFixed(1) + '%' : '<span class="meta">—</span>'}</td>
          <td class="num">${trendHtml(p.trendPct)}</td>
          <td>${lastSaleHtml(p.lastSale, p.diasSinVenta)}</td>
        </tr>
      `).join('')}
    </tbody>
    <tfoot>
      <tr>
        <td><strong>Total</strong></td>
        <td class="num"><strong>${fmtMoney(totalFiltrado)}</strong></td>
        <td class="num"><strong>${fmtInt(totalCount)}</strong></td>
        <td></td>
        <td class="num"><strong>${totalFiltrado > 0 ? '100%' : '—'}</strong></td>
        <td></td>
        <td></td>
      </tr>
    </tfoot>`;
}

let prodChart = null;

function renderProdChart({ items, totalFiltrado }) {
  const canvas = $('gProdChart');
  if (!canvas) return;
  if (prodChart) { prodChart.destroy(); prodChart = null; }
  const data = items.filter((p) => p.monto > 0);
  if (!data.length) return;

  prodChart = new Chart(canvas.getContext('2d'), {
    type: 'bar',
    data: {
      labels: data.map((p) => p.name),
      datasets: [{
        label: 'Ventas',
        data: data.map((p) => p.monto),
        backgroundColor: 'rgba(34,197,94,.55)',
        borderColor: '#22c55e',
        borderWidth: 1,
      }],
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (c) => fmtMoney(c.parsed.x)
              + (totalFiltrado > 0 ? ` · ${((c.parsed.x / totalFiltrado) * 100).toFixed(1)}% del total` : ''),
          },
        },
      },
      scales: {
        x: { ticks: { color: '#a1a1a1' }, grid: { color: 'rgba(255,255,255,.05)' } },
        y: { ticks: { color: '#ffffff' }, grid: { display: false } },
      },
    },
  });
}

/** Tarjetas de ganador (hoy/semana/mes) + ranking + gráfica de participación. */
export function renderProductos({ allRows, ventasFiltered, now = new Date() }) {
  const today = startOfToday(now);
  const end = addDays(today, 1);
  const byProductAll = groupByProduct(allRows);

  renderWinnerCard('gProdWinDia', winnerInPeriod(byProductAll, today, end), 'Sin ventas hoy');
  renderWinnerCard('gProdWinSemana', winnerInPeriod(byProductAll, startOfWeek(now), end), 'Sin ventas esta semana');
  renderWinnerCard('gProdWinMes', winnerInPeriod(byProductAll, startOfMonth(now), end), 'Sin ventas este mes');

  renderLoserCard('gProdLoseDia', loserInPeriod(byProductAll, today, end, now), 'Sin datos suficientes');
  renderLoserCard('gProdLoseSemana', loserInPeriod(byProductAll, startOfWeek(now), end, now), 'Sin datos suficientes');
  renderLoserCard('gProdLoseMes', loserInPeriod(byProductAll, startOfMonth(now), end, now), 'Sin datos suficientes');

  const ranking = buildRanking(byProductAll, ventasFiltered, now);
  renderRankingTable(ranking);
  renderProdChart(ranking);
}

// ─── Insights por producto ────────────────────────────────────────────────

function bestWeekday(rows) {
  const acc = new Map(); // getDay() → { monto, dates: Set<clave local de día> }
  let min = null, max = null;
  for (const r of rows) {
    if (!r.FECHA) continue;
    if (!min || r.FECHA < min) min = r.FECHA;
    if (!max || r.FECHA > max) max = r.FECHA;
    const k = r.FECHA.getDay();
    if (!acc.has(k)) acc.set(k, { monto: 0, dates: new Set() });
    const a = acc.get(k);
    a.monto += montoDisplay(r);
    a.dates.add(r.FECHA.getFullYear() + '-' + r.FECHA.getMonth() + '-' + r.FECHA.getDate());
  }
  if (!min || (max - min) / MS_DAY < 14) return null; // muy poca historia
  let best = null;
  for (const [k, a] of acc) {
    if (a.dates.size < 2) continue;
    const avg = a.monto / a.dates.size;
    if (!best || avg > best.avg) best = { dayIdx: k, avg };
  }
  if (!best || best.avg <= 0) return null;
  // 7 ene 2024 fue domingo (getDay 0) → 7 + idx cae en el día buscado
  const dayName = new Date(2024, 0, 7 + best.dayIdx).toLocaleDateString('es-MX', { weekday: 'long' });
  return { dayName, avg: best.avg };
}

/**
 * Alertas accionables por producto, en el mismo formato {tone, text} de
 * renderInsights (que escapa el texto completo al renderizar).
 */
export function buildProductInsights({ allRows, ventasFiltered, now = new Date() }) {
  const items = [];
  const byProduct = groupByProduct(allRows);
  if (!byProduct.size) return items;
  const today = startOfToday(now);

  const alertas = [];
  for (const [name, rows] of byProduct) {
    const lastSale = lastSaleOf(rows);
    if (!lastSale) continue;
    const dias = Math.floor((today - startOfToday(lastSale)) / MS_DAY);
    if (dias >= DIAS_PRODUCTO_INACTIVO) continue; // producto retirado: no hacer ruido eterno
    if (dias >= DIAS_SIN_VENTA_WARN) {
      alertas.push({
        tone: dias >= DIAS_SIN_VENTA_BAD ? 'bad' : 'warn',
        text: `«${name}» lleva ${dias} días sin ventas.`,
      });
      continue; // la racha sin ventas ya implica caída — no duplicar alerta
    }
    const trend = weeklyTrend(rows, now);
    if (trend && trend.pct <= TENDENCIA_BAJA_PCT) {
      alertas.push({
        tone: trend.pct <= -50 ? 'bad' : 'warn',
        text: `«${name}» vendió ${fmtMoney(trend.last7)} en los últimos 7 días, ${Math.abs(trend.pct).toFixed(0)}% por debajo de su semana promedio (${fmtMoney(trend.avg)}).`,
      });
    }
  }
  alertas.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'bad' ? -1 : 1));
  items.push(...alertas.slice(0, MAX_ALERTAS_PRODUCTO));

  // Concentración: dependencia de un solo producto en el rango filtrado.
  if (byProduct.size >= 2) {
    let total = 0, top = null;
    for (const [name, rows] of groupByProduct(ventasFiltered)) {
      const monto = rows.reduce((s, r) => s + montoDisplay(r), 0);
      total += monto;
      if (!top || monto > top.monto) top = { name, monto };
    }
    if (top && total > 0) {
      const pct = (top.monto / total) * 100;
      if (pct >= CONCENTRACION_PCT) {
        items.push({
          tone: 'warn',
          text: `El ${pct.toFixed(0)}% de tus ventas en este rango depende de «${top.name}» — riesgo de concentración.`,
        });
      }
    }
  }

  const best = bestWeekday(allRows);
  if (best) {
    items.push({
      tone: 'info',
      text: `Tu mejor día histórico es ${best.dayName}: promedio de ${fmtMoney(best.avg)} en ventas por día.`,
    });
  }

  return items;
}
